// Camera plumbing for the barcode scanners.
//
// vue-qrcode-reader defaults its `constraints` prop to literally
// `{ facingMode: "environment" }` -- no resolution, no focus hint, no lens
// choice. On a phone with one rear lens that is fine. On a phone with three it
// is a coin toss: `environment` is satisfied by the ultra-wide, the macro and
// the depth sensor just as well as by the main camera, and several of those are
// fixed-focus. A fixed-focus lens cannot focus at the distance you hold a phone
// to read a serial number, so the preview is permanently soft and nothing ever
// decodes. That is the "works on a Pixel, blurry on other phones" report: a
// Pixel has effectively one rear lens to choose from.
//
// Everything here is pure -- no Vue, no DOM beyond the media APIs -- so the
// scanner component can stay readable.

const DEVICE_KEY = 'scannerDeviceId';

// A serial in Code 128 is a long strip of narrow bars. Held at arm's length it
// might span 15% of the frame; at 640x480 that is ~96px for ~20 bars, under the
// ~2px per module a decoder needs, and no amount of focus will save it. At 1920
// the same strip is ~290px. Asking for resolution is the cheapest single win
// available here.
//
// `ideal` rather than `exact` or `min` throughout: `ideal` is a fitness hint,
// so the UA picks the closest mode it has and never throws. An exact request is
// an OverconstrainedError on the first phone that lacks that exact mode.
const IDEAL_WIDTH = 1920;
const IDEAL_HEIGHT = 1080;

/**
 * MediaTrackConstraints for the scanner stream.
 *
 * @param {string|null} deviceId A specific camera to open, or null to let the
 *   UA pick a rear-facing one.
 * @return {MediaTrackConstraints}
 */
export function buildConstraints(deviceId) {
    const video = {
        width: { ideal: IDEAL_WIDTH },
        height: { ideal: IDEAL_HEIGHT },
        // `advanced` is best-effort by spec: the UA applies each dictionary only
        // if it can satisfy the whole thing, and silently skips the ones it
        // cannot. Chromium on Android honours focusMode; Safari and Firefox do
        // not know the member and ignore it. At the top level the same request
        // would be a hard constraint and could throw, so it lives here.
        advanced: [{ focusMode: 'continuous' }],
    };

    if (deviceId) {
        // deviceId and facingMode must not both appear. On a multi-lens phone
        // the saved id is often a lens the UA does not itself label
        // `environment`, and the pair is then unsatisfiable. deviceId is the
        // more specific request, so it wins outright -- and the caller drops it
        // and retries on failure, see the OverconstrainedError path in the
        // scanner component.
        video.deviceId = { exact: deviceId };
    } else {
        // `ideal`, never `exact`: exact:'environment' throws on a laptop with
        // only a front camera, which would break /scan on the desk.
        video.facingMode = { ideal: 'environment' };
    }

    return video;
}

/**
 * What the browser will tell us about this camera's focusing.
 *
 * Three-valued on purpose. Chromium on Android reports `focusMode` honestly, so
 * there an empty or missing entry really does mean a fixed-focus sensor. Safari
 * and Firefox do not implement the capability at all, and treating their
 * silence as "cannot focus" would send the scanner hunting through every lens
 * on a phone whose camera was working perfectly -- several seconds of black
 * preview, on every open, to fix nothing. So absent means unknown, and unknown
 * means leave well alone.
 *
 * @param {Partial<MediaTrackCapabilities>} capabilities
 * @return {'autofocus'|'fixed'|'unknown'}
 */
export function focusSupport(capabilities) {
    const modes = capabilities?.focusMode;
    if (!Array.isArray(modes) || modes.length === 0) return 'unknown';
    return modes.includes('continuous') || modes.includes('single-shot')
        ? 'autofocus'
        : 'fixed';
}

// There is no standard "this is the back camera" flag on MediaDeviceInfo, so
// the label is all we have. Android Chrome writes "camera2 0, facing back";
// other browsers use "Back Camera" or "Rear Camera". Match loosely and accept
// false positives -- picking a wrong-but-focusing lens is recoverable, skipping
// the right one is not.
const REAR_LABEL = /\b(back|rear|environment)\b/i;

function isRearCamera(device) {
    if (REAR_LABEL.test(device.label || '')) return true;
    // Firefox puts facingMode on the InputDeviceInfo capabilities instead.
    const caps = device.getCapabilities?.();
    return Array.isArray(caps?.facingMode) && caps.facingMode.includes('environment');
}

/**
 * The rear-facing cameras, best-effort.
 *
 * Only meaningful AFTER a camera permission has been granted for this origin:
 * before that every `label` is the empty string and every device looks alike,
 * so calling this early yields a list that cannot be filtered. The scanner
 * calls it from its `camera-on` handler for exactly that reason.
 *
 * Falls back to every video input when no label parses as rear-facing, so a
 * phone whose labels we cannot read is still left with something to try.
 *
 * @return {Promise<MediaDeviceInfo[]>}
 */
export async function listRearCameras() {
    let devices = [];
    try {
        devices = await navigator.mediaDevices.enumerateDevices();
    } catch (e) {
        return [];
    }

    const videoInputs = devices.filter((d) => d.kind === 'videoinput' && d.deviceId);
    const rear = videoInputs.filter(isRearCamera);
    return rear.length > 0 ? rear : videoInputs;
}

/**
 * The camera that last proved it can focus, if we ever found one.
 *
 * Every localStorage access is guarded: the accessor itself throws in some
 * privacy modes, and the scanner must open regardless. Losing the preference
 * only costs one round of probing.
 *
 * @return {string|null}
 */
export function loadPreferredDeviceId() {
    try {
        return localStorage.getItem(DEVICE_KEY) || null;
    } catch (e) {
        return null;
    }
}

export function savePreferredDeviceId(deviceId) {
    if (!deviceId) return;
    try {
        localStorage.setItem(DEVICE_KEY, deviceId);
    } catch (e) {
        // Unwritable -- the scanner still works, it just probes again next time.
    }
}

export function clearPreferredDeviceId() {
    try {
        localStorage.removeItem(DEVICE_KEY);
    } catch (e) {
        // Nothing to do; the caller has already stopped using the id.
    }
}

// error.name -> i18n key. The first four were already mapped by hand in
// ScanQRCode.vue; the rest are names vue-qrcode-reader v5 throws that nothing
// in the app had ever handled, because the @init handler they would have
// reached was the v3 API and never fired.
const CAMERA_ERROR_KEYS = {
    NotAllowedError: 'scan.cameraDenied',
    NotFoundError: 'scan.noCamera',
    NotSupportedError: 'scan.notSupported',
    NotReadableError: 'scan.cameraInUse',
    SecurityError: 'scan.cameraDenied',
    OverconstrainedError: 'scan.cameraUnavailable',
    AbortError: 'scan.cameraUnavailable',
    StreamApiNotSupportedError: 'scan.notSupported',
    InsecureContextError: 'scan.insecureContext',
    StreamLoadTimeoutError: 'scan.cameraTimeout',
};

/**
 * An i18n key for a camera failure, or null when the name is unknown -- the
 * caller then falls back to `scan.initError` plus the raw message, which is
 * what the old hand-rolled mapping did for anything it did not recognise.
 *
 * @param {Error} error
 * @return {string|null}
 */
export function mapCameraError(error) {
    return CAMERA_ERROR_KEYS[error?.name] || null;
}
